import { module } from 'modujs'
import { STATE as FORM_STATE } from './Form'

const CLASS = {
    ACTIVE: 'is-active'
}

/**
 * Tab List
 *
 * Based on {@link https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/Tab_Role example}
 * from the Mozilla Developer Network (MDN).
 *
 * The first tab is assumed to be active.
 */
export default class extends module {
    /**
     * Log standard information.
     *
     * @type {boolean}
     */
    DEBUG = false;

    constructor(m) {
        super(m)

        this.events = {
            click: {
                'tab': 'onTabClick'
            }
        }
    }

    init() {
        this.DEBUG && console.log('[App.Tablist] Initializing…')

        this.tabFocus = 0

        // Enable arrow navigation between tabs in the tab list
        const tabList = this.$('list')[0]
        if (tabList) {
            this.onListKeyDown = this.onListKeyDown.bind(this)
            tabList.addEventListener('keydown', this.onListKeyDown)
        }
    }

    /**
     * @param  {Event} event - The keydown event.
     * @return {void}
     */
    onListKeyDown(event)  {
        // this.DEBUG && console.group('[App.Tablist.onListKeyDown]', event)

        const moveRight = (event.keyCode === 39)
        const moveLeft  = (event.keyCode === 37)

        // Move right or left
        if (moveRight || moveLeft) {
            const tabs = this.$('tab')

            tabs[this.tabFocus].setAttribute('tabindex', -1)

            switch (true) {
                case moveRight:
                    this.tabFocus++
                    // If we're at the end, go to the start
                    if (this.tabFocus >= tabs.length) {
                        this.tabFocus = 0
                    }
                    break

                case moveLeft:
                    this.tabFocus--
                    // If we're at the start, move to the end
                    if (this.tabFocus < 0) {
                        this.tabFocus = tabs.length - 1
                    }
                    break
            }

            tabs[this.tabFocus].setAttribute('tabindex', 0)
            tabs[this.tabFocus].focus()
        }

        // this.DEBUG && console.groupEnd()
    }

    /**
     * @param  {Event} event - The click event.
     * @return {void}
     */
    onTabClick(event)  {
        // this.DEBUG && console.group('[App.Tablist.changeTabs]', event)

        this.changeTabs(event.curTarget)

        // this.DEBUG && console.groupEnd()
    }

    /**
     * @param  {HTMLElement} targetTab - The selected tab button.
     * @return {void}
     */
    changeTabs(targetTab)  {
        this.tabFocus = Array.from(targetTab.parentNode.children).indexOf(targetTab)

        const relatedPanel = this.$('panel')[this.tabFocus]
        if (relatedPanel) {
            Array.from(this.$('panel')).forEach((panel) => {
                panel.setAttribute('hidden', true)
                panel.classList.remove(CLASS.ACTIVE)
                panel.disabled = true
            })

            Array.from(this.$('tab')).forEach((tab) => {
                tab.setAttribute('tabindex', -1)
                tab.setAttribute('aria-selected', false)
                tab.classList.remove(CLASS.ACTIVE)
            })

            const relatedForm = targetTab.closest('form[data-module-form]');
            if (relatedForm) {
                const modId = relatedForm.getAttribute('data-module-form');
                this.call('setState', FORM_STATE.IDLE, 'Form', modId);
            }

            this.showTab(targetTab, relatedPanel)
        } else {
            console.warn('[App.Tablist.changeTabs]', `Tab or panel for tab index ${this.tabFocus} not found`)
        }
    }

    /**
     * @param  {HTMLElement} tab   - The tab button to show.
     * @param  {HTMLElement} panel - The tab content to show.
     * @return {void}
     */
    showTab(tab, panel)  {
        panel.removeAttribute('hidden')
        panel.classList.add(CLASS.ACTIVE)
        panel.disabled = false

        tab.setAttribute('tabindex', 0)
        tab.setAttribute('aria-selected', true)
        tab.classList.add(CLASS.ACTIVE)
    }

    destroy()  {
        const tabList = this.$('list')[0]
        if (tabList) {
            tabList.removeEventListener('keydown', this.onListKeyDown)
        }
    }
}
